import { turkishToUpper, turkishToLower, turkishSort } from './stringUtils';

// Kanun kısaltması -> tam ad (görüntü/tooltip için). Kısaltma = kanonik anahtar.
export const kanunKisaltmalari = {
  TCK: 'Türk Ceza Kanunu',
  CMK: 'Ceza Muhakemesi Kanunu',
  CMUK: 'Ceza Muhakemeleri Usulü Kanunu',
  TMK: 'Türk Medeni Kanunu',
  TBK: 'Türk Borçlar Kanunu',
  HMK: 'Hukuk Muhakemeleri Kanunu',
  HUMK: 'Hukuk Usulü Muhakemeleri Kanunu',
  İİK: 'İcra ve İflas Kanunu',
  İYUK: 'İdari Yargılama Usulü Kanunu',
  TEBLİGAT: 'Tebligat Kanunu',
  KVKK: 'Kişisel Verilerin Korunması Kanunu',
  VUK: 'Vergi Usul Kanunu',
  İYK: 'İş Kanunu',
};

// Kanun numarası -> kanonik kısaltma (ya da özel etiket).
export const kanunNumaralari = {
  '5237': 'TCK',
  '5271': 'CMK',
  '1412': 'CMUK',
  '4721': 'TMK',
  '6098': 'TBK',
  '6100': 'HMK',
  '1086': 'HUMK',
  '2004': 'İİK',
  '2577': 'İYUK',
  '7201': 'TEBLİGAT',
  '6698': 'KVKK',
  '213': 'VUK',
  '4857': 'İYK',
  '765': '765 sayılı TCK',
  '5275': '5275 sayılı Kanun',
};

// Bilinen kanunların görüntü sırası; gerisi alfabetik (TR).
const KANUN_SIRA = [
  'TCK', 'CMK', 'CMUK', 'TMK', 'TBK', 'HMK', 'HUMK',
  'İİK', 'İYUK', 'TEBLİGAT', 'KVKK', 'VUK', 'İYK',
];

const bosParse = (raw) => ({
  raw: typeof raw === 'string' ? raw : '',
  kanunKey: '',
  kanunLabel: '',
  maddeNo: '',
  fikra: '',
  bent: '',
  maddeKey: '',
  canonical: '',
  parsed: false,
});

/**
 * Bir kanun-madde metnini yapısal parçalara ayırır.
 * Örn: "5237 sayılı kanun m. 50/1-a" -> { kanunKey:'TCK', maddeNo:'50', fikra:'1',
 *      bent:'a', maddeKey:'TCK m. 50', canonical:'TCK m. 50/1-a', parsed:true }
 * Obje girişini ({value,label}) ve madde numarası olmayan/çöp girişleri güvenle ele alır.
 */
export function parseKanunMaddesi(girdi) {
  const raw =
    typeof girdi === 'string'
      ? girdi
      : (girdi && (girdi.value != null ? girdi.value : girdi.label)) || '';
  if (!raw || typeof raw !== 'string' || !raw.trim()) return bosParse(raw);

  // TR'ye duyarlı büyük harf + tek boşluk
  let s = turkishToUpper(raw).replace(/\s+/g, ' ').trim();

  let kanunKey = '';

  // 1) Baş kısaltma (harf bloğu) — "M/MD/MADDE" madde işaretidir, kanun değil
  const kisaltmaMatch = s.match(/^([A-ZÇĞİÖŞÜ]+)/);
  if (kisaltmaMatch && !['M', 'MD', 'MADDE'].includes(kisaltmaMatch[1])) {
    kanunKey = kisaltmaMatch[1];
    s = s.slice(kisaltmaMatch[1].length).trim();
  } else {
    // 2) "<n> SAYILI ..." kalıbı
    const sayiliMatch = s.match(/(\d+)\s*SAYILI/);
    // 3) Baş sayı ("5237 m. 50")
    const basSayiMatch = s.match(/^(\d+)/);
    if (sayiliMatch) {
      kanunKey = kanunNumaralari[sayiliMatch[1]] || `${sayiliMatch[1]} sayılı Kanun`;
      s = s.replace(/.*SAYILI(\s+KANUN)?/, '').trim();
    } else if (basSayiMatch) {
      kanunKey = kanunNumaralari[basSayiMatch[1]] || `${basSayiMatch[1]} sayılı Kanun`;
      s = s.slice(basSayiMatch[1].length).trim();
    }
  }

  // Kısaltmayı kanonik forma çek (numara->kısaltma zaten yapıldı; harf bloğu için doğrula)
  if (kanunKey && kanunNumaralari[kanunKey]) {
    kanunKey = kanunNumaralari[kanunKey];
  }

  // Madde işaretini at: "M.", "MD", "MADDE"
  s = s.replace(/^(?:MADDE|MD|M)\b\.?\s*/, '').trim();

  // Madde / fıkra / bent
  const m = s.match(/(\d+)(?:\s*\/\s*(\d+))?(?:\s*-\s*([A-ZÇĞİÖŞÜ]+))?/);
  if (!m) {
    // Madde numarası yok -> sınıflandırılamaz (çöp üretme)
    return {
      ...bosParse(raw),
      kanunKey,
      kanunLabel: kanunKey,
      parsed: false,
    };
  }

  const maddeNo = m[1];
  const fikra = m[2] || '';
  const bent = m[3] ? turkishToLower(m[3]) : '';

  // Kanun tespit edilemediyse sınıflandırılamaz say (yalnız madde no anlamlı değil)
  if (!kanunKey) {
    return { ...bosParse(raw), maddeNo, fikra, bent, parsed: false };
  }

  const maddeKey = `${kanunKey} m. ${maddeNo}`;
  const canonical = `${kanunKey} m. ${maddeNo}${fikra ? '/' + fikra : ''}${bent ? '-' + bent : ''}`;

  return {
    raw,
    kanunKey,
    kanunLabel: kanunKey,
    maddeNo,
    fikra,
    bent,
    maddeKey,
    canonical,
    parsed: true,
  };
}

/** Kanun kısaltmasının tam adı (varsa); tooltip için. */
export function kanunTamAdi(kanunKey) {
  return kanunKisaltmalari[kanunKey] || kanunKey;
}

/** Kanunları görüntü sırasına göre karşılaştırır (bilinenler önce, sonra TR alfabetik). */
export function compareKanun(a, b) {
  const ia = KANUN_SIRA.indexOf(a);
  const ib = KANUN_SIRA.indexOf(b);
  if (ia !== -1 || ib !== -1) {
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    if (ia !== ib) return ia - ib;
  }
  return turkishSort(a, b);
}

/** Madde numaralarını sayısal karşılaştırır. */
export function compareMaddeNo(a, b) {
  return (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0);
}

/** Varyantları doğal sırala: önce fıkra (sayısal, boş önce), sonra bent (TR alfabetik). */
export function compareVaryant(a, b) {
  const fa = a.fikra ? parseInt(a.fikra, 10) : -1;
  const fb = b.fikra ? parseInt(b.fikra, 10) : -1;
  if (fa !== fb) return fa - fb;
  return turkishSort(a.bent || '', b.bent || '');
}

/**
 * Bir parse edilmiş madde, seçili token'ı (seviye:anahtar) karşılıyor mu?
 * token örnekleri: "kanun:TCK", "madde:TCK m. 50", "varyant:TCK m. 50/1-a".
 * Önek yoksa eski (düz-string) seçim kabul edilir -> birebir eşleşme.
 */
export function secimEslesirMi(parsed, token) {
  if (!parsed || !token) return false;
  const idx = token.indexOf(':');
  if (idx === -1) {
    return parsed.canonical === token || parsed.raw === token;
  }
  const level = token.slice(0, idx);
  const key = token.slice(idx + 1);
  if (level === 'kanun') return !!parsed.kanunKey && parsed.kanunKey === key;
  if (level === 'madde') return !!parsed.maddeKey && parsed.maddeKey === key;
  if (level === 'varyant') return parsed.canonical === key || parsed.raw === key;
  return parsed.raw === key;
}
